"use client";

import { cache, ReactNode, useState } from "react";
import {
  HydrationBoundary,
  QueryClient,
  QueryClientConfig,
  QueryClientProvider,
  type DehydratedState,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

interface Props {
  children: ReactNode;
  dehydratedState?: DehydratedState | null;
}

const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
};

export function ReactQueryProvider({ children, dehydratedState }: Props) {
  const [queryClient] = useState(() => new QueryClient(queryClientConfig));

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState ?? undefined}>
        {children}
      </HydrationBoundary>
      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-right" />
      )}
    </QueryClientProvider>
  );
}

export const getQueryClient = cache(() => {
  return new QueryClient(queryClientConfig);
});
